import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'access_token';

// Endpoints that must NOT be retried through the refresh flow — they either
// establish/tear down the session themselves or explicitly report 401 as data.
const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/logout', '/auth/refresh', '/auth/me'];

axios.defaults.withCredentials = true;

axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Silent-refresh interceptor -------------------------------------------
// On any 401 from an API call (other than the auth endpoints themselves),
// try /api/auth/refresh once. If it succeeds, replay the original request
// with the new token. If it fails, clear the session, redirect to /login and
// surface a friendly toast — so an expired session NEVER masquerades as a
// generic feature failure like "Bulk upload failed".
let refreshInFlight = null;
let sessionExpiredHandled = false;

function isAuthPath(url = '') {
  return AUTH_PATHS.some((p) => url.includes(p));
}

async function performRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true, _skipAuthRetry: true })
      .then((res) => {
        const newToken = res?.data?.access_token;
        if (newToken) sessionStorage.setItem(TOKEN_KEY, newToken);
        return true;
      })
      .catch(() => false)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || '';

    if (status !== 401 || original._skipAuthRetry || isAuthPath(url)) {
      return Promise.reject(error);
    }

    original._skipAuthRetry = true;
    const ok = await performRefresh();
    if (ok) {
      const fresh = sessionStorage.getItem(TOKEN_KEY);
      if (fresh) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${fresh}`;
      }
      return axios(original);
    }

    // Refresh failed → the session is truly gone. Show the toast ONCE and
    // bounce the user to /login so they can start fresh.
    if (!sessionExpiredHandled && typeof window !== 'undefined') {
      sessionExpiredHandled = true;
      try {
        sessionStorage.removeItem(TOKEN_KEY);
        toast.error('Your session has expired. Please sign in again.');
      } catch (_) {}
      if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/f/')) {
        setTimeout(() => { window.location.href = '/login'; }, 800);
      }
      setTimeout(() => { sessionExpiredHandled = false; }, 5000);
    }
    return Promise.reject(error);
  }
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true, _skipAuthRetry: true });
      setUser(data);
    } catch (error) {
      setUser(false);
      sessionStorage.removeItem(TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const register = async (name, email, password, phone, extras = {}) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { name, email, password, phone, ...extras },
        { withCredentials: true, _skipAuthRetry: true }
      );
      if (data.access_token) sessionStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true, _skipAuthRetry: true }
      );
      if (data.access_token) sessionStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data);
      sessionExpiredHandled = false;
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, _skipAuthRetry: true });
    } catch (error) {
      console.warn('Logout request failed, clearing local session anyway:', error?.message);
    } finally {
      sessionStorage.removeItem(TOKEN_KEY);
      setUser(false);
    }
  };

  const value = { user, loading, register, login, logout, checkAuth };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
